import { useMemo } from "react";

import { useForm } from "react-hook-form";

import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextAreaInput } from "@shared/textarea-input";
import { maxCharacterLength, notEmpty } from "@shared/validators";

import { PromotionCalendarDatePicker } from "./promotion-calendar-annotation-date-picker";
import { PromotionCalendarCampaignType } from "./promotion-calendar-campaign-type";

export interface NewPromotionCalendar {
  description: string;
  annotatedDate: string;
  endDate: string;
  campaignType: string;
}

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  isLoading: boolean;
  onSubmit: ({
    description,
    annotatedDate,
    endDate,
    campaignType,
  }: {
    description: string;
    annotatedDate: string;
    endDate: string;
    campaignType: string;
  }) => Promise<void>;
};

export const PromotionCalendarModal = ({
  isOpen,
  closeModal,
  isLoading,
  onSubmit,
}: Props) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<NewPromotionCalendar>({
    defaultValues: {
      description: "",
      annotatedDate: "",
      endDate: "",
      campaignType: "",
    },
  });

  const description = watch("description");
  const annotatedDate = watch("annotatedDate");
  const endDate = watch("endDate");
  const campaignType = watch("campaignType");
  const currentPromotionCalendar = useMemo<NewPromotionCalendar>(
    () => ({
      description,
      annotatedDate,
      endDate,
      campaignType,
    }),
    [description, annotatedDate, endDate, campaignType],
  );

  const _onSubmit = async () => {
    await handleSubmit(onSubmit)();
    reset();
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      titleContent="Add New Event"
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <form
        className="flex flex-col space-y-4 mt-5 mb-1"
        data-testid="annotation-form"
      >
        <PromotionCalendarDatePicker
          watch={watch}
          setValue={setValue}
          currentPromotionCalendar={currentPromotionCalendar}
          control={control}
          errors={errors}
        />
        <PromotionCalendarCampaignType
          currentPromotionCalendar={currentPromotionCalendar}
          control={control}
          errors={errors}
        />
        <TextAreaInput
          {...register("description", {
            required: "Description is required",
            validate: {
              notEmpty: notEmpty(),
              maxCharacterLength: maxCharacterLength({ maxLength: 300 }),
            },
          })}
          defaultValue={currentPromotionCalendar.description}
          errors={errors}
          rows={6}
          label="NOTE"
          placeholder="Type your event name or description here..."
        />
      </form>
      <div className="flex justify-end mt-4">
        <TertiaryButton loading={isLoading} onClick={closeModal}>
          CANCEL
        </TertiaryButton>
        <PrimaryButton
          loading={isLoading}
          onClick={_onSubmit}
          isDisabled={!isValid || isSubmitting}
        >
          ADD EVENT
        </PrimaryButton>
      </div>
    </Modal>
  );
};
